import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import { api } from "../lib/api";

function uid() {
  return "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("tt_chat_session");
    if (!s) { s = uid(); localStorage.setItem("tt_chat_session", s); }
    return s;
  });
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hallo! 👋 Ik ben de digitale assistent van TT Motorsports. Vragen over onderhoud, onderdelen, onze motoren of een afspraak? Ik help je graag verder!" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  useEffect(() => {
    const o = () => setOpen((v) => !v);
    window.addEventListener("tt-open-chat", o);
    return () => window.removeEventListener("tt-open-chat", o);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await api.post("/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, ik ben even niet beschikbaar. Bel ons gerust op 0485 74 15 24." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen(!open)}
        className="hidden lg:flex fixed lg:bottom-6 lg:right-6 z-[60] w-14 h-14 bg-[#E10600] hover:bg-[#B80500] text-white items-center justify-center shadow-2xl transition-transform hover:scale-105"
        aria-label={open ? "Chat sluiten" : "Chat openen"}
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-[76px] left-3 right-3 sm:left-auto sm:right-4 lg:bottom-24 lg:right-6 z-[60] sm:w-[380px] h-[520px] max-h-[calc(100dvh-170px)] lg:max-h-[70vh] bg-[#141518] border border-[#2A2B31] flex flex-col shadow-2xl"
        >
          <div className="bg-[#0A0A0B] px-4 py-3 flex items-center gap-3 border-b border-[#2A2B31]">
            <div className="w-9 h-9 bg-[#E10600] flex items-center justify-center">
              <MessageSquare size={18} className="text-white" />
            </div>
            <div>
              <p className="font-display uppercase font-700 text-white text-sm tracking-wide leading-tight">TT Assistent</p>
              <p className="text-[10px] text-[#9A9CA3] font-mono-tt flex items-center gap-1.5">
                <span className="w-2 h-2 bg-green-500 rounded-full inline-block" /> Online
              </p>
            </div>
            <button type="button" onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Chat sluiten"
              className="ml-auto w-11 h-11 flex items-center justify-center text-[#9A9CA3] hover:text-white">
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#E10600] text-white"
                      : "bg-[#1C1D22] text-[#E6E6E8] border border-[#2A2B31]"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1C1D22] border border-[#2A2B31] px-4 py-3 flex gap-1.5">
                  <span className="w-2 h-2 bg-[#9A9CA3] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-[#9A9CA3] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-[#9A9CA3] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-[#2A2B31] flex gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Stel je vraag..."
              className="flex-1 bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]"
            />
            <button
              data-testid="chat-send"
              onClick={send}
              disabled={loading}
              className="bg-[#E10600] hover:bg-[#B80500] disabled:opacity-50 text-white px-3.5 flex items-center justify-center transition-colors"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
